/**
 * Trip Secure + Insurance cover, as shown in the account settings dialog.
 *
 * Content only — this is marketing copy describing an insurance product, so it
 * is kept in one place rather than inlined in the dialog. The counts rendered in
 * the UI are derived from these arrays, never written by hand, so a benefit
 * added here can't leave a stale "8 benefits" label behind.
 */
export const TRIP_SECURE_BENEFITS = [
  {
    key: "domesticFlights",
    label: "Domestic flights",
    benefits: [
      {
        title: "Delayed/lost baggage assistance",
        description: "Get assistance in case your checked-in baggage is delayed or lost.",
      },
      {
        title: "Personal accident",
        description:
          "Provides coverage up to the sum insured for death or permanent disability to the insured person resulting from an accident during the trip.",
      },
      {
        title: "Loss of checked-in baggage",
        description:
          "Covers cost of baggage and its contents in case of loss, misdirection or non-delivery of check-in baggage by the airline.",
      },
      {
        title: "Delay of checked-in baggage",
        description:
          "Covers you if your checked-in baggage is delayed by more than 4 hours from the expected time of delivery by the airline.",
      },
      {
        title: "Missed connection",
        description: "Covers you if a connecting flight has been missed due to cancellation.",
      },
      {
        title: "Trip cancellation",
        description:
          "If the trip is cancelled before the start date due to medical reasons of the insured person, their companion or an immediate family member.",
      },
      {
        title: "Diverted flight",
        description: "When the flight is diverted due to emergency situations.",
      },
      {
        title: "Trip delay",
        description: "In case the flight is delayed by 120 minutes or more.",
      },
    ],
  },
  {
    key: "domesticHotels",
    label: "Domestic hotels",
    benefits: [
      {
        title: "Emergency medical assistance",
        description: "Covers ambulance assistance, house call-doctor and teleconsultations.",
      },
      {
        title: "In-patient care",
        description:
          "Covers emergency hospitalisation or medical expenses for any illness or injury during the trip.",
      },
      {
        title: "Medical evacuation",
        description: "Covers expenses of arranging transport or evacuation for medical purposes.",
      },
      {
        title: "Bounced booking",
        description:
          "Covers the additional amount paid for alternate accommodation in case the property denies check-in.",
      },
      {
        title: "Property cancellation",
        description:
          "Covers cancellation charges if cancelled due to illness or natural calamities.",
      },
      {
        title: "Loss of laptop, tablet or hand baggage",
        description: "Covers you in case of loss of laptop, tablet or hand baggage.",
      },
      {
        title: "Emergency property accommodation",
        description:
          "Emergency accommodation for an immediate family member in case the insured is hospitalised.",
      },
      {
        title: "Repatriation of mortal remains",
        description:
          "Covers transportation and funeral expenses of mortal remains back to the place of residence.",
      },
      {
        title: "Personal accident",
        description: "Financial support in case of death or disability due to an accident.",
      },
      {
        title: "Home content burglary",
        description:
          "Covers you in case there is a burglary at your place of residence while you are on the trip, except cash and jewellery.",
      },
      {
        title: "Personal liability",
        description:
          "Covers you against claims resulting from injuries or damages caused to a third party during the trip.",
      },
    ],
  },
  {
    key: "internationalFlights",
    label: "International flights",
    benefits: [
      {
        title: "Emergency medical expenses",
        description:
          "For immediate emergency medical assistance required on account of any illness or injury sustained whilst on a trip, subject to the maximum sum insured in the schedule.",
      },
      {
        title: "Trip cancellation and/or interruption",
        description:
          "If the trip is cancelled before the scheduled departure date, or interrupted due to medical emergencies or reasons mentioned in the policy schedule.",
      },
      {
        title: "Delay of checked-in baggage",
        description:
          "If your checked-in baggage is delayed by more than 12 hours from the expected time of delivery by the airline.",
      },
      {
        title: "Common carrier delay",
        description: "If the flight is delayed by 6 hours or more.",
      },
      {
        title: "Loss of checked-in baggage",
        description:
          "For the total loss of checked-in baggage on a trip. The cover is limited to the travel destinations specified in the proposal form; all halts and via destinations on the ticket are covered.",
      },
      {
        title: "Accidental death",
        description: "If the insured person dies due to an accident whilst on the trip.",
      },
      {
        title: "Dental treatment expenses",
        description:
          "For expenses incurred due to acute anaesthetic treatment of a natural tooth during a trip, as specified in the schedule.",
      },
      {
        title: "Emergency medical evacuation",
        description:
          "For the extra costs of medically necessary transportation or evacuation from the location of the accident to the insured person's place of residence or the nearest hospital.",
      },
      {
        title: "Hijack distress allowance",
        description:
          "In the event of a hijack of a common carrier in which the insured person is travelling.",
      },
      {
        title: "Out-patient expenses",
        description: "OPD expenses for any illness or injury during the trip.",
      },
      {
        title: "Permanent total disability",
        description:
          "If the insured person suffers permanent total disability due to an accident during the trip.",
      },
      {
        title: "Permanent partial disability",
        description:
          "If the insured person suffers any permanent partial disability due to an accident during the trip.",
      },
    ],
  },
  {
    key: "internationalHotels",
    label: "International hotels",
    benefits: [
      {
        title: "Emergency medical expenses",
        description:
          "Covers emergency hospitalisation or medical expenses for any illness or injury during the trip.",
      },
      {
        title: "Medical evacuation",
        description:
          "Covers expenses of arranging transportation or evacuation for medical emergencies.",
      },
      {
        title: "Outpatient expenses",
        description: "Covers you in case of outpatient expenses incurred while you are on a trip.",
      },
      {
        title: "Emergency dental expenses",
        description: "Covers you in case you have a dental emergency while on a trip.",
      },
      {
        title: "Trip cancellation",
        description:
          "If the trip is cancelled before the scheduled departure date due to medical emergencies or reasons mentioned in the policy schedule.",
      },
      {
        title: "Delay of checked-in baggage",
        description:
          "If your checked-in baggage is delayed by more than 12 hours from the expected time of delivery by the airline.",
      },
      {
        title: "Common carrier delay",
        description: "If the flight is delayed by 6 hours or more.",
      },
      {
        title: "Loss of checked-in baggage",
        description:
          "Covers cost of baggage and its contents in case of loss, misdirection or non-delivery of checked-in baggage by the airline.",
      },
      {
        title: "Trip interruption",
        description:
          "If the trip is interrupted during the policy period due to medical emergencies or reasons mentioned in the policy schedule.",
      },
      {
        title: "Loss of passport",
        description:
          "Covers the expenses of getting a duplicate or fresh passport in case of loss of passport during the trip.",
      },
      {
        title: "Missed connection",
        description:
          "Covers expenses incurred if the second of two flights is missed in a connection, due to delay or cancellation of the prior flight.",
      },
      {
        title: "Accidental death",
        description: "If the insured person dies due to an accident whilst on the trip.",
      },
      {
        title: "Permanent total disability",
        description:
          "If the insured person suffers permanent total disability as mentioned in the policy schedule due to an accident whilst on the trip.",
      },
      {
        title: "Repatriation of mortal remains",
        description:
          "Covers transportation and funeral expenses of mortal remains back to the place of residence.",
      },
      {
        title: "Personal liability",
        description:
          "Covers you for losses and damages sustained by a third party, along with related legal costs.",
      },
      {
        title: "Compassionate visit — travel",
        description:
          "In case of your hospitalisation while on a trip, this cover pays for the travel of an immediate family member.",
      },
    ],
  },
];
